/**
 * ApiStatusBadge — topbar pulse showing api-shim + per-vendor health.
 *
 * Probes /api/healthz on mount and every 60s. The endpoint returns the
 * shim's own state plus a passive `vendors` snapshot — each vendor's last
 * real call recorded by the client (`vendor_status.py`). The dot's color is
 * the worst-of state across the shim + every vendor that's seen at least
 * one call. Vendors that haven't been called yet render as "unknown" in
 * the hover panel and don't trigger amber/red on the dot.
 *
 * Hover surfaces a per-vendor breakdown so the user can tell *which*
 * upstream is degraded (Meraki, Aruba, or Anthropic) without leaving the
 * topbar.
 *
 * Why passive instead of synthetic probes: real-traffic-driven status
 * matches user experience exactly (if Meraki just 401'd a real call, the
 * badge turns immediately) and doesn't burn upstream rate limits.
 */
import { useEffect, useReducer, useRef } from "react";
import { createPortal } from "react-dom";
import { get as apiGet } from "@/api/client";
import { cn } from "@/lib/cn";

type VendorTone = "healthy" | "degraded" | "unknown";

interface VendorSnapshot {
  ok: boolean | null; // null = no calls observed yet
  lastCheck: number | null; // epoch seconds
  lastError: string | null;
}

interface HealthResponse {
  ok: boolean;
  service?: string;
  cache_entries?: number;
  vendors?: Record<"meraki" | "aruba" | "anthropic", VendorSnapshot>;
}

const POLL_INTERVAL_MS = 60_000;

/** Map a vendor snapshot to a tone for the dropdown color. */
function vendorTone(s: VendorSnapshot | undefined): VendorTone {
  if (!s) return "unknown";
  if (s.ok === null) return "unknown";
  return s.ok ? "healthy" : "degraded";
}

/** Render epoch seconds as "Xs/m/h ago" relative to now. Returns "—" for null. */
function relativeAgo(epochSec: number | null): string {
  if (!epochSec) return "—";
  const sec = Math.max(0, Math.floor(Date.now() / 1000) - epochSec);
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

type BadgeState = {
  shimStatus: "loading" | "healthy" | "down";
  lastChecked: Date | null;
  vendors: HealthResponse["vendors"] | null;
  open: boolean;
  menuPos: { top: number; left: number };
};

const BADGE_INIT: BadgeState = {
  shimStatus: "loading",
  lastChecked: null,
  vendors: null,
  open: false,
  menuPos: { top: 0, left: 0 },
};

export function ApiStatusBadge() {
  const [{ shimStatus, lastChecked, vendors, open, menuPos }, setBadge] =
    useReducer(
      (s: BadgeState, p: Partial<BadgeState>) => ({ ...s, ...p }),
      BADGE_INIT,
    );
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    let visible = !document.hidden;

    async function probe() {
      // Skip while the tab is backgrounded — no point waking the server
      // every 60s for a tab the user isn't looking at.
      if (cancelled || !visible) return;
      try {
        const res = await apiGet<HealthResponse>("/healthz");
        if (cancelled) return;
        if (res?.ok) {
          setBadge({ shimStatus: "healthy", vendors: res.vendors ?? null, lastChecked: new Date() });
        } else {
          setBadge({ shimStatus: "down", lastChecked: new Date() });
        }
      } catch {
        if (!cancelled) setBadge({ shimStatus: "down", lastChecked: new Date() });
      }
    }

    void probe();
    const id = window.setInterval(probe, POLL_INTERVAL_MS);
    const onVis = () => {
      visible = !document.hidden;
      if (visible) void probe();
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      cancelled = true;
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  // Close the dropdown when clicking outside.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setBadge({ open: false });
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  // Position dropdown in viewport space so it is not clipped by parent stacking contexts.
  useEffect(() => {
    if (!open) return;

    const updateMenuPos = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;
      const width = 260;
      const left = Math.max(
        8,
        Math.min(window.innerWidth - width - 8, rect.right - width),
      );
      setBadge({ menuPos: { top: rect.bottom + 8, left } });
    };

    updateMenuPos();
    window.addEventListener("resize", updateMenuPos);
    window.addEventListener("scroll", updateMenuPos, true);
    return () => {
      window.removeEventListener("resize", updateMenuPos);
      window.removeEventListener("scroll", updateMenuPos, true);
    };
  }, [open]);

  // Aggregate dot color: shim down → red. Any observed vendor failing →
  // amber. All observed vendors healthy → green. Unobserved vendors don't
  // count against the aggregate.
  const aggregate: "loading" | "healthy" | "down" | "degraded" = (() => {
    if (shimStatus === "loading") return "loading";
    if (shimStatus === "down") return "down";
    if (!vendors) return "healthy";
    const tones = Object.values(vendors).map(vendorTone);
    if (tones.some((t) => t === "degraded")) return "degraded";
    return "healthy";
  })();

  const dotClass =
    aggregate === "healthy"
      ? "bg-mint shadow-[0_0_6px_rgba(0,255,180,0.5)]"
      : aggregate === "degraded"
        ? "bg-peach shadow-[0_0_6px_rgba(255,180,80,0.5)]"
        : aggregate === "down"
          ? "bg-rose shadow-[0_0_6px_rgba(255,80,100,0.5)]"
          : "bg-peach";

  const summaryWord =
    aggregate === "healthy"
      ? "healthy"
      : aggregate === "degraded"
        ? "degraded"
        : aggregate === "down"
          ? "down"
          : "probing";

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setBadge({ open: !open })}
        title={`API ${summaryWord} — click for vendor breakdown`}
        className={cn(
          "inline-flex h-9 items-center gap-1.5 rounded-lg border border-line bg-surface px-2.5",
          "text-[11px] text-fg-mute hover:bg-surface-2 hover:text-fg",
        )}
      >
        <span className={cn("h-2 w-2 rounded-full", dotClass)} />
        <span className="font-mono uppercase tracking-wide">api</span>
      </button>

      {open
        ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              style={{ top: menuPos.top, left: menuPos.left }}
              className={cn(
                "fixed z-[9999] w-[260px] overflow-hidden",
                "rounded-lg border border-line bg-surface shadow-2xl",
              )}
            >
              <div className="border-b border-line px-3 py-2">
                <div className="text-[12px] font-semibold text-fg">
                  API status
                </div>
                <div className="mt-0.5 font-mono text-[10px] text-fg-mute">
                  shim · {shimStatus} · last probe{" "}
                  {lastChecked ? lastChecked.toLocaleTimeString() : "—"}
                </div>
              </div>

              <ul className="m-0 list-none p-0">
                {(["meraki", "aruba", "anthropic"] as const).map((v) => {
                  const snap = vendors?.[v];
                  const tone = vendorTone(snap);
                  const dot =
                    tone === "healthy"
                      ? "bg-mint"
                      : tone === "degraded"
                        ? "bg-peach"
                        : "bg-fg-mute/40";
                  return (
                    <li
                      key={v}
                      className="flex items-start gap-2 border-t border-line/60 px-3 py-2 first:border-t-0"
                    >
                      <span
                        className={cn(
                          "mt-1.5 h-2 w-2 shrink-0 rounded-full",
                          dot,
                        )}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2 text-[12px]">
                          <span className="font-semibold text-fg capitalize">
                            {v}
                          </span>
                          <span className="font-mono text-[10px] text-fg-mute">
                            {tone === "unknown"
                              ? "no calls yet"
                              : relativeAgo(snap?.lastCheck ?? null)}
                          </span>
                        </div>
                        {tone === "degraded" && snap?.lastError ? (
                          <div
                            className="mt-0.5 truncate font-mono text-[10px] text-rose"
                            title={snap.lastError}
                          >
                            {snap.lastError}
                          </div>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
